import React, { lazy, Suspense, useEffect } from 'react';
import { Route, Routes, useLocation, useParams } from 'react-router-dom';
import { ROUTE_PARAM_KEYS, ReportRouteParams } from 'src/types/routes/routes.types';
import { ADMIN_ROUTES, DEV_ROUTES, NAV_ITEMS } from '../constants/routingConstants';
import { AdminAccessLevel } from '../types/admin/adminNavigation.types';
import { PRM_REPORT_UNIQUE_CODE } from 'src/components/reports/prm-detailed-report/prmDetailedReportConstants';
import { PRM_CHANGE_REPORT_UNIQUE_CODE } from 'src/components/reports/prm-change-report/prmChangeReportConstants';
import { ASIN_ESSENTIALS_REPORT_ROUTE } from 'src/components/reports/asin-essentials-report/asinEssentialsReportConstants';
import { ASIN_MAPPING_EXCEPTIONS_REPORT_ROUTE } from 'src/components/reports/asin-mapping-exceptions/asinMappingExceptionsConstants';
import { trackPageView, normalizePathname } from '../utils/metrics';

// Route guards and error boundaries (small, needed upfront)
import AdminRouteGuard from '../components/admin/AdminRouteGuard';
import AppRouteGuard from '../components/auth/AppRouteGuard';
import { AppErrorBoundary } from '../components/common/AppErrorBoundary';
import { RouteLoadingFallback } from '../components/common/RouteLoadingFallback';
import ReportRouteGuard from 'src/components/reports/ReportRouteGuard';

// Lazy-loaded route components (code splitting)
const HomePage = lazy(() => import('../pages/HomePage'));
const AccessManagement = lazy(() => import('../components/access-management/AccessManagement'));
const AccessManagementForm = lazy(() => import('src/components/access-management/AccessManagementForm'));
const AdminDefaultRedirect = lazy(() => import('../components/admin/AdminDefaultRedirect'));
const ReportConfiguration = lazy(() => import('src/components/admin/reports-configuration/ReportConfigurationHome'));
const ReportConfigurationForm = lazy(
  () => import('src/components/admin/reports-configuration/ReportConfigurationForm')
);
const FinanceContactsHomePage = lazy(() =>
  import('src/components/finance-contacts/FinanceContactsHomePage').then((module) => ({
    default: module.FinanceContactsHomePage,
  }))
);
const GanttChartWrapper = lazy(() => import('src/components/gantt-chart/GanttChartWrapper'));
const ManageAttributes = lazy(() => import('src/components/manage-attributes/ManageAttributes'));
const BulkEditHomePage = lazy(() =>
  import('../components/manage-programs/bulk-edit-product-hierarchy/BulkEditHomePage').then((module) => ({
    default: module.BulkEditHomePage,
  }))
);
const ViewEditProgramsRedirect = lazy(() =>
  import('src/components/manage-programs/ViewEditProgramsRedirect').then((module) => ({
    default: module.ViewEditProgramsRedirect,
  }))
);
const ProductLineContainer = lazy(() =>
  import('../components/manage-programs/product-line/ProductLineContainer').then((module) => ({
    default: module.ProductLineContainer,
  }))
);
const FpnContainer = lazy(() =>
  import('../components/manage-programs/rpn-fpn/FpnContainer').then((module) => ({ default: module.FpnContainer }))
);
const RpnContainer = lazy(() =>
  import('../components/manage-programs/rpn-fpn/RpnContainer').then((module) => ({ default: module.RpnContainer }))
);
const ReportViewPage = lazy(() =>
  import('src/components/reports/ReportViewPage').then((module) => ({ default: module.ReportViewPage }))
);
const PrmDetailedReportPage = lazy(() =>
  import('src/components/reports/prm-detailed-report/PrmDetailedReportPage').then((module) => ({
    default: module.PrmDetailedReportPage,
  }))
);
const PrmChangeReportPage = lazy(() =>
  import('src/components/reports/prm-change-report/PrmChangeReportPage').then((module) => ({
    default: module.PrmChangeReportPage,
  }))
);
const AsinEssentialsReportPage = lazy(() =>
  import('src/components/reports/asin-essentials-report/AsinEssentialsReportPage').then((module) => ({
    default: module.AsinEssentialsReportPage,
  }))
);
const AsinMappingExceptionsPage = lazy(() =>
  import('src/components/reports/asin-mapping-exceptions/AsinMappingExceptionsPage').then((module) => ({
    default: module.AsinMappingExceptionsPage,
  }))
);
const ScenarioSnapshot = lazy(() => import('src/components/scenario-snapshots/ScenarioSnapshot'));
const SupportContacts = lazy(() =>
  import('src/components/support-contacts/SupportContacts').then((module) => ({ default: module.SupportContacts }))
);
const AuditLogsPage = lazy(() =>
  import('src/components/audit-logs/AuditLogsPage').then((module) => ({ default: module.AuditLogsPage }))
);
const ProductLineManagement = lazy(() => import('../components/admin/product-line-management/ProductLineManagement'));
const UserMetricsPage = lazy(() => import('../components/kpi-metrics/KpiMetricsPage'));
const ChangeHistoryPage = lazy(() => import('../components/change-history-page/ChangeHistoryPage'));

const RouteTracker: React.FC = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    trackPageView(normalizePathname(pathname));
  }, [pathname]);

  return null;
};

/**
 * AppRoutes component handles all routing configuration for the application.
 *
 * PERFORMANCE OPTIMIZATION:
 * All route components are lazy-loaded using React.lazy() to enable code splitting.
 * This reduces the initial bundle size by ~80-85% by loading routes on-demand.
 *
 * Admin Route Access Levels:
 * - ADMIN_ROUTES: Accessible by isVerityAdmin OR isDeveloper
 * - DEV_ROUTES: Accessible only by isDeveloper
 */
const AppRoutes: React.FC = () => {
  return (
    <AppErrorBoundary>
      <Suspense fallback={<RouteLoadingFallback />}>
        <RouteTracker />
        <Routes>
          {/* Application-level route guard - protects all routes */}
          <Route element={<AppRouteGuard />}>
            {/* Basic route with app-level protection */}
            <Route path={NAV_ITEMS.HOME.href} element={<HomePage />} />

            {/*
             * ===== PROGRAM MANAGEMENT ROUTES =====
             *
             * 1. View/Edit Programs (/manage-programs/*)
             *    - For viewing and editing individual programs
             *    - Base path redirects to most relevant detail page (FPN > RPN > Product Line)
             *    - Has nested routes for product-line, rpn, and fpn detail pages
             *
             * 2. Bulk Edit (/bulk-edit)
             *    - Shows BulkEditHomePage with Product Hierarchy table for bulk operations
             *    - No nested routes - clicking items navigates to View/Edit Programs routes
             *    - Acts as a landing page for bulk selection/operations
             *
             * 3. Manage Attributes (/manage-attributes)
             *    - For managing program attributes
             */}

            {/* View/Edit Programs - redirects to the most relevant detail page (FPN > RPN > Product Line) */}
            <Route path={NAV_ITEMS.VIEW_EDIT_PROGRAMS.href} element={<ViewEditProgramsRedirect />} />
            {/* View/Edit Programs nested routes - handles all /manage-programs/* routes */}
            <Route path={`${NAV_ITEMS.VIEW_EDIT_PROGRAMS.href}/*`} element={<ViewEditProgramsRoutes />} />

            {/*
             * Bulk Edit - shows BulkEditHomePage with Product Hierarchy table for bulk operations.
             * Note: No nested routes needed - item clicks navigate to View/Edit Programs routes.
             */}
            <Route path={NAV_ITEMS.BULK_EDIT.href} element={<BulkEditHomePage />} />

            {/* Manage Attributes */}
            <Route path={NAV_ITEMS.MANAGE_ATTRIBUTES.href} element={<ManageAttributes />} />

            {/*
             * ===== PLANNING & INSIGHTS ROUTES =====
             */}
            <Route path={NAV_ITEMS.PROGRAM_ROADMAP.href} element={<GanttChartWrapper />} />

            {/*
             * ===== ADMIN TOOLS ROUTES =====
             * These routes are visible in HomePageSideNavigation "Admin Tools" section
             * Accessible by isVerityAdmin OR isDeveloper
             */}
            <Route element={<AdminRouteGuard requiredAccessLevel={AdminAccessLevel.ADMIN} />}>
              {/* Scenario Snapshots */}
              <Route path={NAV_ITEMS.SCENARIO_SNAPSHOTS.href} element={<ScenarioSnapshot />} />
              {/* Access Management */}
              <Route path={ADMIN_ROUTES.ACCESS_MANAGEMENT.href} element={<AccessManagement />} />
              <Route path={ADMIN_ROUTES.ACCESS_MANAGEMENT_CREATE.href} element={<AccessManagementForm />} />
              <Route
                path={`${ADMIN_ROUTES.ACCESS_MANAGEMENT_EDIT.href}/:${ROUTE_PARAM_KEYS.ACCESS_ID}`}
                element={<AccessManagementForm />}
              />
            </Route>

            {/*
             * ===== SUPPORT ROUTES =====
             * Note: External links (Documentation Wiki, Contact tool support, Update program in PPLS)
             * are handled in side navigation and don't require routes.
             */}
            <Route path={NAV_ITEMS.FINANCE_CONTACTS.href} element={<FinanceContactsHomePage />} />
            <Route path={`/verity-support`} element={<SupportContacts />} />

            {/* Change History - dedicated AG-Grid page for all change history */}
            <Route path={NAV_ITEMS.CHANGE_HISTORY.href} element={<ChangeHistoryPage />} />

            {/*
             * ===== REPORTS ROUTES =====
             * All reports share a single guarded route pattern:
             *   /reports/:report_unique_code → ReportRouteGuard → ReportContentRouter
             *
             * ReportRouteGuard validates the code and checks visibility-level access.
             * ReportContentRouter dispatches to the appropriate page component:
             *   - "PRM_DETAILED" → PrmDetailedReportPage (custom data pipeline)
             *   - everything else → ReportViewPage (generic S3 report viewer)
             */}
            {/*
             * ASIN Essentials Report — static path registered BEFORE the
             * param route so it takes precedence. It has no backend
             * ReportConfig record yet (S3 location is hardcoded per stage),
             * so it intentionally bypasses ReportRouteGuard's config check.
             */}
            <Route path={ASIN_ESSENTIALS_REPORT_ROUTE} element={<AsinEssentialsReportPage />} />
            <Route path={ASIN_MAPPING_EXCEPTIONS_REPORT_ROUTE} element={<AsinMappingExceptionsPage />} />

            <Route path={`/reports/:${ROUTE_PARAM_KEYS.REPORT_UNIQUE_CODE}`} element={<ReportRouteGuard />}>
              <Route index element={<ReportContentRouter />} />
            </Route>

            {/*
             * ===== ADMIN ROUTES =====
             * Routes are protected by AdminRouteGuard with specific access levels:
             * - ADMIN: Accessible by isVerityAdmin OR isDeveloper
             * - DEVELOPER: Accessible only by isDeveloper
             */}

            {/* Default /admin redirect - handled by AdminDefaultRedirect component */}
            <Route path="/admin" element={<AdminDefaultRedirect />} />

            {/* DEV_ROUTES: Accessible only by isDeveloper */}
            <Route element={<AdminRouteGuard requiredAccessLevel={AdminAccessLevel.DEVELOPER} />}>
              <Route path={DEV_ROUTES.REPORTS_CONFIGURATION.href} element={<ReportConfiguration />} />
              <Route path={DEV_ROUTES.REPORTS_CONFIGURATION_CREATE.href} element={<ReportConfigurationForm />} />
              <Route
                path={`${DEV_ROUTES.REPORTS_CONFIGURATION_EDIT.href}/:${ROUTE_PARAM_KEYS.REPORT_UNIQUE_CODE}`}
                element={<ReportConfigurationForm />}
              />
              <Route path={DEV_ROUTES.PRODUCT_LINE_MANAGEMENT.href} element={<ProductLineManagement />} />
              <Route path={DEV_ROUTES.AUDIT_LOGS.href} element={<AuditLogsPage />} />
              <Route path={DEV_ROUTES.USER_METRICS.href} element={<UserMetricsPage />} />
            </Route>

            {/* Additional routes will be added here as the application grows */}
          </Route>
        </Routes>
      </Suspense>
    </AppErrorBoundary>
  );
};

/**
 * ReportContentRouter - Routes to the appropriate report page based on the report unique code.
 * Custom report pages bypass the generic S3 report viewer for better performance:
 *   - "PRM_DETAILED" → PrmDetailedReportPage (custom data pipeline with scenario merging)
 *   - "PRM_CHANGE"   → PrmChangeReportPage (product-line-level S3 partitioned data)
 *   - everything else → ReportViewPage (generic S3 report viewer)
 */
const ReportContentRouter: React.FC = () => {
  const { report_unique_code } = useParams<ReportRouteParams>();

  if (report_unique_code === PRM_REPORT_UNIQUE_CODE) {
    return <PrmDetailedReportPage />;
  }

  if (report_unique_code === PRM_CHANGE_REPORT_UNIQUE_CODE) {
    return <PrmChangeReportPage />;
  }

  return <ReportViewPage />;
};

/**
 * Nested routes for View/Edit Programs
 *
 * URL Structure:
 * /manage-programs - Redirects to most relevant detail page (via ViewEditProgramsRedirect)
 * /manage-programs/product-code/:productCode - Product line details (e.g., /manage-programs/product-code/ABC123)
 * /manage-programs/product-code/:productCode/rpn/:rpnId - RPN details (e.g., /manage-programs/product-code/ABC123/rpn/R456)
 * /manage-programs/product-code/:productCode/rpn/:rpnId/fpn/:fpnId - FPN details (e.g., /manage-programs/product-code/ABC123/rpn/R456/fpn/F00789)
 *
 * PERFORMANCE: These nested routes are also lazy-loaded for optimal performance.
 */
const ViewEditProgramsRoutes: React.FC = () => {
  return (
    <Suspense fallback={<RouteLoadingFallback />}>
      <Routes>
        <Route path={`product-code/:${ROUTE_PARAM_KEYS.PRODUCT_CODE}`} element={<ProductLineContainer />} />
        <Route
          path={`product-code/:${ROUTE_PARAM_KEYS.PRODUCT_CODE}/rpn/:${ROUTE_PARAM_KEYS.RPN_ID}`}
          element={<RpnContainer />}
        />
        <Route
          path={`product-code/:${ROUTE_PARAM_KEYS.PRODUCT_CODE}/rpn/:${ROUTE_PARAM_KEYS.RPN_ID}/fpn/:${ROUTE_PARAM_KEYS.FPN_ID}`}
          element={<FpnContainer />}
        />
      </Routes>
    </Suspense>
  );
};

export default AppRoutes;
